import React, { useMemo, useState, useRef } from "react";
import OrderDetails from "./OrderDetails";
import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import api from "../../../../middleware/api";
import { toast } from "react-toastify";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import PortalModal from "../../../Common/Modal/PortalModal";
import OrderStatus from "../../../Common/OrderStatus/OrderStatus";
import LoadingSpinner from "../../../Common/Loading/LoadingSpinner";
import { useAuth } from "../../../../context/AuthContext";
import { formatDateWithTimezone } from "../../../../utils/dateFormatter";


const CapsuleRefCell = ({ row }) => {
    if (row.original.type === "order_capsule") {
        const ref = row.original.relatedCapsules.map(capsule => (
            <div key={capsule.id}>
                <p>{`${capsule.name} : #${capsule.id}`}</p>
            </div>
        ))
        return <div >{ref}</div>
    }
    return <p>-</p>
}



const OrdersTable = ({ orders, setOrders }) => {
    const { user } = useAuth()
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [sorting, setSorting] = useState([]);
    const [activeTab, setActiveTab] = useState("all");
    const [columnVisibility, setColumnVisibility] = useState({
        customer: true,
        therapist: user.permission.label === "Admin" ? true : false,
        total: true,
        status: true,
        creationDate: true,
        pay: false,
    });
    const modalRef = useRef();

    // Mettre à jour la visibilité des colonnes en fonction de l'onglet actif
    const updateColumnVisibility = (tab) => {
        if (tab === "all") {
            setColumnVisibility({
                customer: true,
                therapist: user.permission.label === "Admin" ? true : false,
                total: true,
                status: true,
                creationDate: true,
                pay: true,
            });
        } else if (tab === "order_capsule") {
            setColumnVisibility({
                customer: true,
                therapist: false,
                total: true,
                status: true,
                creationDate: true,
                pay: false,
            });
        } else if (tab === "order_refill_account" || tab === "order_product") {
            setColumnVisibility({
                customer: false,
                therapist: true,
                total: true,
                status: true,
                creationDate: true,
                pay: true,
            });
        }
    };

    // Filtrer les commandes en fonction de l'onglet actif
    const filteredOrders = useMemo(() => {
        if (activeTab === "order_refill_account") {
            return orders.filter(order => order.type === "order_refill_account");
        } else if (activeTab === "order_product") {
            return orders.filter(order => order.type === "order_product");
        } else if (activeTab === "order_capsule") {
            return orders.filter(order => order.type === "order_capsule");
        }
        return orders; // Onglet "all"
    }, [orders, activeTab]);
    // Définition des colonnes avec des identifiants
    const columns = useMemo(
        () => [
            {
                id: "wcId",
                accessorKey: "wcId",
                header: "Order ID",
                cell: (info) => `#${info.getValue()}`,
            },
            {
                id: 'capsuleRef',
                accessorKey: 'capsuleRef',
                header: 'Ref. Capsule',
                cell: (info) => <CapsuleRefCell row={info.row} />
            },
            {
                id: "customer",
                accessorKey: "customer.firstname",
                header: "Client",
                cell: (info) => `${info.row.original?.customer?.firstname || ""} ${info.row.original?.customer?.lastname || ""}`,

            },
            {
                id: "therapist",
                accessorKey: "therapist.firstname",
                header: "Thérapeute",
                cell: (info) => `${info.row.original?.therapist?.firstname || ""} ${info.row.original?.therapist?.lastname || ""}`,

            },
            {
                id: "total",
                accessorKey: "total",
                header: "Total",
                cell: (info) => `${info.getValue()} €`,

            },
            {
                id: "status",
                accessorKey: "status",
                header: "Status",
                cell: (info) => {
                    return <OrderStatus status={info.getValue()} />
                },

            },
            {
                id: "creationDate",
                accessorKey: "creationDate",
                header: "Date",
                cell: (info) => {
                    return formatDateWithTimezone(info.getValue());
                },
            },
            {
                id: "pay",
                accessorKey: "pay",
                enableSorting: false,
                header: "Action",
                cell: (info) => {
                    const status = info.row.original.status;
                    const orderType = info.row.original.type;
                    const paymentButton = (status === "pending" && orderType !== "order_capsule") ?
                        (<button onClick={() => handlePayment(info.row.original.paymentUrl)} className="btn btn-primary btn-xs">Payer</button>)
                        : null;
                    // const cancelButton = (status === "pending" && orderType !== "order_capsule") ?
                    //     (<button onClick={() => handleCancelConfirmation(info.row.original.id)} disabled={loading} className="btn btn-error btn-xs">{loading ? <CircleStackIcon className="w-4 h-4 animate-spin" /> : "Annuler"}</button>)
                    //     : null;
                    return (
                        <div className="flex flex-col gap-2">
                            {paymentButton}                        </div>
                    );
                },

            },
        ],
        [columnVisibility] // Ajout de columnVisibility comme dépendance
    );
    const handlePayment = (paymentUrl) => {
        if (paymentUrl) {
            window.open(paymentUrl, '_blank');
        }
    };
    const [orderToCancel, setOrderToCancel] = useState(null);

    const handleCancel = async (orderId) => {
        try {

            setLoading(true);
            const response = await api.get(`/orders/${orderId}/cancel`);
            if (response.data.success) {
                toast.success("Commande annulée avec succès");
                const orderToUpdate = orders.find(order => order.id === orderId);
                orderToUpdate.status = "cancelled";
                setOrders([...orders.filter(order => order.id !== orderId), orderToUpdate]);
            }
        } catch (error) {
            toast.error("Une erreur est survenue lors de l'annulation de la commande");
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelConfirmation = (orderId) => {
        modalRef.current.showModal();
        setOrderToCancel(orderId);
    };

    const confirmCancel = async () => {
        if (orderToCancel) {
            await handleCancel(orderToCancel);
            modalRef.current.hideModal();
        }
    };

    // Mettre à jour la visibilité des colonnes lorsque l'onglet actif change
    React.useEffect(() => {
        updateColumnVisibility(activeTab);
    }, [activeTab]);

    const table = useReactTable({
        data: filteredOrders,
        columns,
        state: { sorting, columnVisibility },
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const toggleRowExpansion = (rowId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    };

    return (
        <div>
            <div role="tablist" className="tabs tabs-lifted mb-4 p-1 rounded-lg">
                <a
                    role="tab"
                    onClick={() => setActiveTab("order_capsule")}
                    className={`tab flex-1 transition-all duration-200 font-medium rounded-lg ${activeTab === "order_capsule" ? "tab-active !bg-primary text-primary-content" : ""}`}
                >
                    Capsules
                </a>
                <a
                    role="tab"
                    onClick={() => setActiveTab("order_refill_account")}
                    className={`tab flex-1 transition-all duration-200 font-medium rounded-lg ${activeTab === "order_refill_account" ? "tab-active !bg-primary text-primary-content" : ""}`}
                >
                    Recharges
                </a>
                <a
                    role="tab"
                    onClick={() => setActiveTab("order_product")}
                    className={`tab flex-1 transition-all duration-200 font-medium rounded-lg ${activeTab === "order_product" ? "tab-active !bg-primary text-primary-content" : "hover:bg-base-300"}`}
                >
                    Produits
                </a>
                <a
                    role="tab"
                    onClick={() => setActiveTab("all")}
                    className={`tab flex-1 transition-all duration-200 font-medium rounded-lg ${activeTab === "all" ? "tab-active !bg-primary text-primary-content" : "hover:bg-base-300"}`}
                >
                    Toutes
                </a>
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
                <table className="table table-zebra w-full border-collapse">
                    <thead className="bg-primary text-primary-content">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    header.column.getIsVisible() && ( // Vérifier la visibilité de la colonne
                                        <th
                                            key={header.id}
                                            className="py-3 px-4 text-center cursor-pointer flex-row"
                                            onClick={
                                                header.column.getCanSort()
                                                    ? header.column.getToggleSortingHandler()
                                                    : undefined
                                            }
                                        >
                                            <div className="flex flex-row items-center justify-center gap-2">

                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                {header.column.getIsSorted() ? (
                                                    header.column.getIsSorted() === "asc" ? (
                                                        <ArrowUpIcon className="w-4 h-4" />
                                                    ) : (
                                                        <ArrowDownIcon className="w-4 h-4" />
                                                    )
                                                ) : null}
                                            </div>
                                        </th>
                                    )
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <React.Fragment key={row.id}>
                                <tr
                                    className="hover:bg-base-200 cursor-pointer"
                                    onClick={() => toggleRowExpansion(row.id)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        cell.column.getIsVisible() && ( // Vérifier la visibilité de la cellule
                                            <td
                                                key={cell.id}
                                                className="py-3 px-4 text-center"
                                            >

                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        )
                                    ))}
                                </tr>
                                {expandedRows[row.id] && (
                                    <tr className="bg-base-100 transition-all duration-300 ease-in-out">
                                        <td colSpan={table.getAllColumns().length}>
                                            <OrderDetails order={row.original} />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <PortalModal ref={modalRef} onClose={() => setOrderToCancel(null)}>
                <h3>Confirmation</h3>
                <p>Êtes-vous sûr de vouloir annuler cette commande ?</p>
                <div className="flex justify-end gap-2">
                    <button onClick={confirmCancel} disabled={loading} className="btn btn-sm btn-error">
                        {loading && <LoadingSpinner style="small" />}
                        Confirmer
                    </button>
                    <button onClick={() => modalRef.current.hideModal()} disabled={loading} className="btn btn-sm">Annuler</button>
                </div>
            </PortalModal>
        </div>
    );
};

export default OrdersTable;
